import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { CurateWordmark } from "@/components/Brand";
import { toast } from "sonner";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1c4c9bcc-15eb-4aff-af44-10f350d3e934/images/c67fa1085085b76bc6ae43d5db3be333f120429ae9791d65a6b4991032d36c8c.png";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: image */}
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <CurateWordmark size={28} link={false} onDark />
          <div>
            <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95]">
              The shop floor,<br />reimagined.
            </h2>
            <p className="mt-4 max-w-md text-white/80 text-sm leading-relaxed">
              Sign in to manage your brand, products and customer experiences.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-8 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="label-overline">← Back</Link>
          <h1 className="mt-6 font-display font-black tracking-tighter text-4xl">Sign in</h1>
          <p className="mt-2 text-sm text-[#52525b]">Welcome back. Manage your brand experience.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label className="label-overline">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="login-email"
                placeholder="you@brand.com"
              />
            </div>
            <div>
              <Label className="label-overline">Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="login-password"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-8 w-full h-12 rounded-none bg-[#0F1210] hover:bg-black text-white text-base"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="mt-6 text-sm text-[#52525b]">
            New here?{" "}
            <Link to="/register" className="text-[#0F1210] underline" data-testid="link-to-register">
              Create a brand account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
